import React, { useEffect, useMemo, useState } from "react";
import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, Target, ClipboardList, CalendarClock, Receipt,
  Settings, LogOut, ExternalLink, Menu, X, Search, Bell, HelpCircle, Plus,
  ChevronRight, ChevronDown, MoreHorizontal, Handshake, Boxes, ListTodo, FileCheck2,
  BarChart3, Wrench, Ticket, ShoppingBag,
} from "lucide-react";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const PRIMARY_NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Accueil", tid: "nav-admin-dashboard" },
  { to: "/admin/requests", icon: ClipboardList, label: "Inbox", tid: "nav-admin-requests" },
  { to: "/admin/ticketing", icon: Ticket, label: "Billetterie", tid: "nav-admin-ticketing" },
  { to: "/admin/marketplace", icon: ShoppingBag, label: "Marketplace", tid: "nav-admin-marketplace" },
  { to: "/admin/deals", icon: Target, label: "Transactions", tid: "nav-admin-deals" },
  { to: "/admin/listings", icon: Building2, label: "Annonces", tid: "nav-admin-listings" },
  { to: "/admin/bookings", icon: CalendarClock, label: "Calendrier", tid: "nav-admin-bookings" },
];

const MORE_NAV = [
  { to: "/admin/invoices", icon: Receipt, label: "Finance", tid: "nav-admin-finance" },
  { to: "/admin/partners", icon: Handshake, label: "Partenaires", tid: "nav-admin-partners" },
  { to: "/admin/documents", icon: FileCheck2, label: "Documents", tid: "nav-admin-documents" },
  { to: "/admin/reports", icon: BarChart3, label: "Rapports", tid: "nav-admin-reports" },
  { to: "/admin/settings", icon: Settings, label: "Paramètres", tid: "nav-admin-settings" },
];

const LEGACY_NAV = [
  { to: "/admin/leads", icon: Users, label: "Contacts", tid: "nav-admin-leads" },
  { to: "/admin/visits", icon: ClipboardList, label: "Visites terrain", tid: "nav-admin-visits" },
  { to: "/admin/tasks", icon: ListTodo, label: "Tâches & relances", tid: "nav-admin-tasks" },
  { to: "/admin/catalog", icon: Boxes, label: "Catalogue partenaires", tid: "nav-admin-catalog" },
  { to: "/admin/services", icon: Wrench, label: "Catalogue services", tid: "nav-admin-services" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [requestNotifications, setRequestNotifications] = useState([]);
  const [ownerData, setOwnerData] = useState({ requests: [], leads: [], listings: [], visits: [] });

  useEffect(() => {
    const secondaryPaths = [...MORE_NAV, ...LEGACY_NAV].map((item) => item.to);
    if (secondaryPaths.some((path) => location.pathname.startsWith(path))) setMoreOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    Promise.all([
      api.get("/requests"), api.get("/leads"), api.get("/listings", { params: { status: "", limit: 100 } }), api.get("/visits"),
    ]).then(([requests, leads, listings, visits]) => setOwnerData({
      requests: requests.data || [], leads: leads.data || [], listings: listings.data || [], visits: visits.data || [],
    })).catch(() => {});
  }, [location.pathname]);

  useEffect(() => {
    const loadNotifications = () => api.get("/notifications", { params: { limit: 30 } }).then((response) => setRequestNotifications(response.data || [])).catch(() => {});
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 15000);
    return () => window.clearInterval(timer);
  }, []);

  const searchResults = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (value.length < 2) return [];
    const matches = (text) => String(text || "").toLowerCase().includes(value);
    return [
      ...ownerData.requests.filter((item) => matches(`${item.number} ${item.name} ${item.phone} ${item.email} ${item.listing_title}`)).map((item) => ({ id: `request-${item.id}`, label: item.name, detail: `Demande ${item.number}`, to: "/admin/requests" })),
      ...ownerData.leads.filter((item) => matches(`${item.name} ${item.phone} ${item.email}`)).map((item) => ({ id: `lead-${item.id}`, label: item.name, detail: "Prospect CRM", to: `/admin/leads?lead=${item.id}` })),
      ...ownerData.listings.filter((item) => matches(`${item.title} ${item.city} ${item.zone}`)).map((item) => ({ id: `listing-${item.id}`, label: item.title, detail: "Annonce", to: `/admin/listings/${item.sector === "auto" ? "auto" : item.sector === "hospitality" ? "stay" : item.sector === "business" ? "business" : "immo"}/${item.id}` })),
    ].slice(0, 8);
  }, [ownerData, search]);

  const operationalNotifications = useMemo(() => {
    const now = new Date();
    const due = ownerData.requests.filter((item) => item.reminder_at && new Date(item.reminder_at) <= now && item.status !== "termine");
    const fresh = ownerData.requests.filter((item) => item.status === "nouveau");
    const today = now.toISOString().slice(0, 10);
    const visits = ownerData.visits.filter((item) => item.date === today && item.status !== "completed" && item.status !== "cancelled");
    return [
      ...due.map((item) => ({ id: `due-${item.id}`, label: `Relancer ${item.name}`, detail: item.number, to: "/admin/requests", tone: "text-amber-700" })),
      ...fresh.map((item) => ({ id: `new-${item.id}`, label: `Nouvelle demande de ${item.name}`, detail: item.number, to: "/admin/requests", tone: "text-blue-700" })),
      ...visits.map((item) => ({ id: `visit-${item.id}`, label: "Visite prévue aujourd'hui", detail: item.time || "", to: "/admin/visits", tone: "text-emerald-700" })),
    ].slice(0, 12);
  }, [ownerData]);

  const notifications = useMemo(() => [
    ...requestNotifications.map((item) => ({ ...item, label: item.title, detail: item.message, to: item.action_link || "/admin/requests", tone: item.read ? "text-slate-600" : "text-blue-700", persistent: true })),
    ...operationalNotifications,
  ].slice(0, 30), [requestNotifications, operationalNotifications]);
  const unreadCount = requestNotifications.filter((item) => !item.read).length + operationalNotifications.length;

  const openNotification = async (item) => {
    if (item.persistent && !item.read) {
      await api.patch(`/notifications/${item.id}/read`).catch(() => {});
      setRequestNotifications((current) => current.map((entry) => entry.id === item.id ? { ...entry, read: true } : entry));
    }
    navigate(item.to);
    setNotificationOpen(false);
  };

  const readAllNotifications = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setRequestNotifications((current) => current.map((item) => ({ ...item, read: true })));
  };

  const NavItem = ({ it, small = false }) => (
    <NavLink
      key={it.label}
      to={it.to}
      end={it.end}
      data-testid={it.tid}
      onClick={() => setDrawerOpen(false)}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 ${small ? "py-2" : "py-2.5"} rounded-lg text-[13px] transition-colors ${
          isActive
            ? "bg-white text-brand-navy font-semibold shadow-sm ring-1 ring-slate-200"
            : "text-slate-600 hover:bg-white hover:text-brand-navy"
        }`
      }
    >
      <it.icon className="h-4 w-4" strokeWidth={2} />
      <span className="truncate">{it.label}</span>
    </NavLink>
  );

  const Sidebar = () => (
    <>
      <div className="border-b border-slate-200 px-5 py-4">
        <Logo size="md" />
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" aria-label="Navigation Owner">
        {PRIMARY_NAV.map((it) => <NavItem key={it.tid} it={it} />)}
        <button
          type="button"
          onClick={() => setMoreOpen((value) => !value)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] text-slate-600 transition-colors hover:bg-white hover:text-brand-navy"
          aria-expanded={moreOpen}
          data-testid="nav-admin-more"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="flex-1 text-left">Plus</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
        </button>
        {moreOpen && (
          <div className="mt-1 space-y-1 rounded-xl bg-slate-100/80 p-1.5">
            {MORE_NAV.map((it) => <NavItem key={it.tid} it={it} small />)}
            <div className="mx-2 my-1 border-t border-slate-200" />
            <div className="px-3 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Outils</div>
            {LEGACY_NAV.map((it) => <NavItem key={it.tid} it={it} small />)}
          </div>
        )}
      </nav>
      <div className="border-t border-slate-200 p-3">
        <div className="rounded-xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-full bg-brand-orange text-white flex items-center justify-center font-bold">
              {(user?.name || "O").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-brand-navy truncate">Owner</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email || "Administrateur"}</div>
            </div>
          </div>
          <div className="mt-2.5 grid grid-cols-2 gap-1.5">
            <a href="/" target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-brand-navy flex items-center gap-1 px-2 py-1.5 rounded hover:bg-slate-50" data-testid="admin-view-site">
              <ExternalLink className="h-3 w-3" /> Site
            </a>
            <button
              onClick={async () => { await logout(); navigate("/admin/login"); }}
              className="text-[11px] text-slate-500 hover:text-red-600 flex items-center gap-1 px-2 py-1.5 rounded hover:bg-red-500/10"
              data-testid="admin-logout"
            >
              <LogOut className="h-3 w-3" /> Sortir
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const seg = location.pathname.split("/").filter(Boolean);
  const crumb = seg.length <= 1 ? "Accueil"
    : seg[1] === "deals" ? "Transactions"
    : seg[1] === "leads" ? "Contacts"
    : seg[1] === "requests" ? "Inbox"
    : seg[1] === "ticketing" ? "Billetterie"
    : seg[1] === "visits" ? "Visites terrain"
    : seg[1] === "bookings" ? "Calendrier"
    : seg[1] === "invoices" ? "Finance"
    : seg[1] === "listings" ? "Annonces"
    : seg[1] === "partners" ? "Partenaires & Apporteurs"
    : seg[1] === "catalog" ? "Catalogue partenaire"
    : seg[1] === "services" ? "Catalogue Services"
    : seg[1] === "tasks" ? "Tâches & Relances"
    : seg[1] === "documents" ? "Documents & Vérification"
    : seg[1] === "reports" ? "Rapports"
    : seg[1] === "settings" ? "Paramètres"
    : seg[1];

  return (
    <div className="admin-scope min-h-[100dvh] bg-[#f7f7f5] flex">
      <aside className="hidden lg:flex w-[244px] flex-shrink-0 border-r border-slate-200 bg-[#f4f4f2] text-brand-navy flex-col">
        <Sidebar />
      </aside>

      {drawerOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setDrawerOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] border-r border-slate-200 bg-[#f4f4f2] text-brand-navy flex flex-col" data-testid="admin-mobile-drawer">
            <div className="flex items-center justify-end p-2">
              <button onClick={() => setDrawerOpen(false)} className="h-9 w-9 rounded-full hover:bg-slate-200 flex items-center justify-center" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Sidebar />
          </aside>
        </>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 min-h-14 lg:min-h-16 bg-white border-b border-slate-200 flex items-center gap-3 px-3 sm:px-4 lg:px-6 pt-safe">
          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden h-9 w-9 rounded-lg bg-brand-mist flex items-center justify-center text-slate-700"
            aria-label="Menu"
            data-testid="admin-menu-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>

          <nav className="hidden lg:flex items-center gap-1.5 text-[13px]" aria-label="Fil d'Ariane">
            <span className="text-slate-500">Team Work</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            <span className="font-semibold text-brand-navy">{crumb}</span>
          </nav>

          <div className="hidden md:flex flex-1 max-w-md ml-4 relative">
            <label className="w-full flex items-center gap-2 h-9 rounded-lg bg-brand-mist px-3">
              <Search className="h-4 w-4 text-slate-400" />
              <input value={search} onChange={(event) => { setSearch(event.target.value); setSearchOpen(true); }} onFocus={() => setSearchOpen(true)} onKeyDown={(event) => { if (event.key === "Enter" && searchResults[0]) { navigate(searchResults[0].to); setSearchOpen(false); } }} placeholder="Rechercher une demande, un client, une annonce..." className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400" />
            </label>
            {searchOpen && search.trim().length >= 2 && (
              <div className="absolute left-0 right-0 top-11 z-50 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
                {searchResults.length ? searchResults.map((item) => (
                  <button key={item.id} onClick={() => { navigate(item.to); setSearchOpen(false); }} className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left hover:bg-slate-50">
                    <span><span className="block text-sm font-semibold text-brand-navy">{item.label}</span><span className="block text-[11px] text-slate-400">{item.detail}</span></span>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </button>
                )) : <div className="px-3 py-4 text-sm text-slate-400">Aucun résultat</div>}
              </div>
            )}
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={() => setSearchOpen((value) => !value)} className="md:hidden h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" aria-label="Recherche"><Search className="h-4 w-4" /></button>
            <div className="relative">
              <button onClick={() => setNotificationOpen((value) => !value)} className="relative h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" aria-label="Notifications">
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && <span className="absolute right-1 top-1 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{unreadCount > 99 ? "99+" : unreadCount}</span>}
              </button>
              {notificationOpen && <div className="absolute right-0 top-11 z-50 w-[min(92vw,380px)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"><div className="flex items-center justify-between border-b border-slate-100 px-4 py-3"><span className="text-sm font-semibold text-brand-navy">Notifications</span><button onClick={readAllNotifications} className="text-[11px] font-semibold text-brand-orange">Tout lire</button></div><div className="max-h-[60vh] overflow-y-auto p-2">{notifications.length ? notifications.map((item) => <button key={item.id} onClick={() => openNotification(item)} className="w-full rounded-lg px-3 py-2 text-left hover:bg-slate-50"><div className={`text-xs font-semibold ${item.tone}`}>{item.label}</div><div className="mt-0.5 text-[11px] text-slate-400 line-clamp-2">{item.detail}</div></button>) : <div className="px-3 py-6 text-center text-xs text-slate-400">Aucune notification</div>}</div></div>}
            </div>
            <Link to="/admin/requests" className="hidden sm:inline-flex h-9 items-center gap-2 rounded-lg bg-brand-orange px-3 text-xs font-bold text-white"><Plus className="h-4 w-4" /> Nouvelle demande</Link>
            <LanguageSwitcher />
          </div>
        </header>

        {searchOpen && <div className="md:hidden border-b border-slate-200 bg-white px-3 py-2"><input autoFocus value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher..." className="h-10 w-full rounded-lg bg-brand-mist px-3 text-sm outline-none" />{search.trim().length >= 2 && <div className="mt-2 space-y-1">{searchResults.map((item) => <button key={item.id} onClick={() => { navigate(item.to); setSearchOpen(false); }} className="flex w-full items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-left"><span><span className="block text-sm font-semibold text-brand-navy">{item.label}</span><span className="block text-[11px] text-slate-400">{item.detail}</span></span><ChevronRight className="h-4 w-4 text-slate-300" /></button>)}</div>}</div>}

        <main className="flex-1 min-w-0 p-3 sm:p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
